import { createContext, useContext, useState, useEffect } from "react";

const LangContext = createContext();

const STORAGE_KEY = "ire_lang";
const VALID = ["en", "de"];

// Resolve initial language with priority: URL ?lang= → localStorage → "en"
// Note: browser navigator language intentionally NOT used — English is the
// site-wide default so investors reach identical copy regardless of locale.
const resolveInitialLang = () => {
  try {
    const qp = new URLSearchParams(window.location.search).get("lang");
    if (qp && VALID.includes(qp)) return qp;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.includes(stored)) return stored;
  } catch (e) { /* SSR or restricted env */ }
  return "en";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(resolveInitialLang);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    document.documentElement.lang = lang;
    // Keep ?lang= in URL in sync so links/share preserve language and SEO hreflang resolves cleanly.
    try {
      const url = new URL(window.location.href);
      if (lang === "en") {
        url.searchParams.delete("lang");
      } else {
        url.searchParams.set("lang", lang);
      }
      window.history.replaceState({}, "", url.toString());
    } catch (e) { /* ignore */ }
  }, [lang]);

  // Sync language if user navigates with back/forward (URL param changes)
  useEffect(() => {
    const onPop = () => {
      const qp = new URLSearchParams(window.location.search).get("lang");
      if (qp && VALID.includes(qp) && qp !== lang) setLang(qp);
      else if (!qp && lang !== "en") setLang("en");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [lang]);

  const toggle = () => setLang((l) => (l === "en" ? "de" : "en"));

  return (
    <LangContext.Provider value={{ lang, setLang, toggle }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
